import { http } from "./client";
import type { PageParams } from "@/types/api";
import type {
  Payroll,
  PayrollGenerateResult,
  PayrollRun,
  PayrollRunApproval,
  PayrollRunStatus,
  PayrollStatus,
  TeamPayslipSummary,
} from "@/types/domain";

export interface PayrollRunFilters extends PageParams {
  year?: number;
  status?: PayrollRunStatus | "";
}

export interface PayslipFilters extends PageParams {
  run_id?: string;
  employee_id?: string;
  department_id?: string;
  branch_id?: string;
  period?: string;
  status?: PayrollStatus | "";
}

export const payrollApi = {
  generate: (period: string) => http.post<PayrollGenerateResult>("/payroll/generate", { period }),
  runs: (filters: PayrollRunFilters = {}) => http.getPage<PayrollRun>("/payroll/runs", filters),
  run: (id: string) => http.get<PayrollRun>(`/payroll/runs/${id}`),
  runByPeriod: (period: string) => http.get<PayrollRun>("/payroll/period", { period }),
  lock: (id: string) => http.post<{ run: PayrollRun; message: string }>(`/payroll/runs/${id}/lock`),
  unlock: (id: string, reason: string) => http.post<PayrollRun>(`/payroll/runs/${id}/unlock`, { reason }),
  markPaid: (id: string, payment_reference: string) =>
    http.post<{ message: string }>(`/payroll/runs/${id}/mark-paid`, { payment_reference }),

  // ---- Phase 2: approval workflow ----
  submit: (id: string) => http.post<PayrollRun>(`/payroll/runs/${id}/submit`),
  approve: (id: string, comment?: string) => http.post<PayrollRun>(`/payroll/runs/${id}/approve`, { comment }),
  reject: (id: string, reason: string) => http.post<PayrollRun>(`/payroll/runs/${id}/reject`, { reason }),
  approvalHistory: (id: string) => http.get<PayrollRunApproval[]>(`/payroll/runs/${id}/approval-history`),

  payslips: (filters: PayslipFilters = {}) => http.getPage<Payroll>("/payroll/payslips", filters),
  payslip: (id: string) => http.get<Payroll>(`/payroll/payslips/${id}`),
  myPayslips: () => http.get<Payroll[]>("/payroll/my-payslips"),
  downloadPayslip: (id: string) => http.download(`/payroll/payslips/${id}/pdf`),

  // A manager's redacted view of their direct reports - see PermPayslipTeam.
  teamSummary: (period: string) => http.get<TeamPayslipSummary[]>("/payroll/team-summary", { period }),
};

// ---- One-click payroll: readiness, background generation ----

export type ReadinessSeverity = "blocking" | "warning";

export interface ReadinessIssue {
  employee_id?: string;
  employee_code?: string;
  employee_name?: string;
  code: string;
  severity: ReadinessSeverity;
  message: string;
}

export type ReadinessCheckStatus = "ready" | "warning" | "critical";

/** One row of the pre-check summary (Attendance, Leave, Salary, ...). */
export interface ReadinessCheck {
  key: string;
  label: string;
  status: ReadinessCheckStatus;
  count: number;
  summary: string;
}

/** The month as it would be generated now - the real payroll calculation,
 * run in memory. */
export interface PayrollEstimate {
  gross: string | number;
  deductions: string | number;
  net: string | number;
  payslips: number;
}

export interface PayrollReadiness {
  period: string;
  period_label: string;
  run_id?: string;
  run_status?: PayrollRunStatus;
  can_generate: boolean;
  employees: number;
  ready: number;
  need_attention: number;
  blocking: number;
  checks: ReadinessCheck[];
  estimate?: PayrollEstimate;
  /** Employees with Payroll Review corrections this month (already in the estimate). */
  corrected_employees?: number;
  period_issues: ReadinessIssue[];
  employee_issues: ReadinessIssue[];
  checked_at: string;
}

export type PayrollJobStatus = "queued" | "processing" | "validating" | "completed" | "failed";

export interface PayrollJob {
  id: string;
  period: string;
  period_label: string;
  status: PayrollJobStatus;
  steps: { key: string; label: string; state: "pending" | "running" | "done" }[];
  processed: number;
  total: number;
  percent: number;
  run_id?: string;
  payslips: number;
  failures: number;
  warnings: number;
  error?: string;
  started_at: string;
  updated_at: string;
  finished_at?: string;
  reused?: boolean;
}

export const payrollRunApi = {
  readiness: (period: string) => http.get<PayrollReadiness>("/payroll/readiness", { period }),
  startJob: (period: string) => http.post<PayrollJob>("/payroll/jobs", { period }),
  job: (id: string) => http.get<PayrollJob>(`/payroll/jobs/${id}`),
  latestJob: (period: string) => http.get<{ job: PayrollJob | null }>("/payroll/jobs/latest", { period }),
};

// ---- Payroll Review: employee-level corrections before generating ----

export type CorrectionField =
  | "working_days"
  | "present_days"
  | "paid_leave_days"
  | "lop_days"
  | "overtime_hours"
  | "basic_salary"
  | "bonus"
  | "incentive"
  | "allowance"
  | "adjustment"
  | "loan_deduction"
  | "advance_deduction"
  | "other_deduction"
  | "needs_review"
  /** The final amount of one earning or deduction line (HRA, PF ...): "line:" + the payslip line code. */
  | `line:${string}`;

/** One field of one employee: what the records say, what payroll will use. */
export interface ReviewValue {
  original: number;
  /** The month's global control value for this field, when one is set. */
  global?: number;
  value: number;
  corrected: boolean;
  reason?: string;
}

export type ReviewFilterKey =
  | "all"
  | "needs_correction"
  | "ready"
  | "missing_data"
  | "attendance_issues"
  | "leave_issues"
  | "salary_issues"
  | "corrected";

export interface ReviewRow {
  employee_id: string;
  employee_code: string;
  employee_name: string;
  department?: string;
  department_id?: string;
  branch_id?: string;
  designation?: string;
  fields: Record<CorrectionField, ReviewValue>;
  earnings?: string | number;
  deductions?: string | number;
  net?: string | number;
  status: "ready" | "needs_correction";
  corrected: boolean;
  correction_count: number;
  issues?: ReadinessIssue[];
}

export interface ReviewCounts {
  all: number;
  needs_correction: number;
  ready: number;
  missing_data: number;
  attendance_issues: number;
  leave_issues: number;
  salary_issues: number;
  corrected: number;
}

export interface ReviewPage {
  period: string;
  period_label: string;
  items: ReviewRow[];
  page: { page: number; per_page: number; total: number; total_pages: number; has_next: boolean; has_prev: boolean };
  counts: ReviewCounts;
  estimate?: PayrollEstimate;
  calendar_days: number;
  locked: boolean;
  locked_reason?: string;
  designations: string[];
  matching_ids?: string[];
  /** The month's global payroll controls and the rules behind them. */
  controls?: DetailControls;
}

export interface ReviewQuery {
  period: string;
  search?: string;
  department_id?: string;
  branch_id?: string;
  designation?: string;
  filter?: ReviewFilterKey;
  page?: number;
  per_page?: number;
  include_ids?: boolean;
}

/** value null resets the field to what the records say. */
export interface CorrectionChange {
  employee_id: string;
  field: CorrectionField;
  value: number | null;
  reason?: string;
}

export interface SaveCorrectionsResult {
  saved: number;
  reset: number;
  unchanged: number;
  employees: number;
}

export type BulkMode = "set" | "add" | "reset";

export interface CorrectionHistoryRow {
  id: string;
  employee_id: string;
  field: CorrectionField;
  action: "set" | "reset";
  original_value: number;
  old_value?: number;
  new_value?: number;
  /** The month's global value and the value payroll used, when the change was made. */
  global_value?: number | null;
  effective_value?: number | null;
  reason: string;
  changed_by_name?: string;
  changed_at: string;
}

export const payrollReviewApi = {
  review: (q: ReviewQuery) => http.get<ReviewPage>("/payroll/review", { ...q, include_ids: q.include_ids ? "true" : undefined }),
  save: (period: string, changes: CorrectionChange[]) =>
    http.post<SaveCorrectionsResult>("/payroll/review/corrections", { period, changes }),
  bulk: (input: { period: string; employee_ids: string[]; field: CorrectionField; mode: BulkMode; value?: number; reason?: string }) =>
    http.post<SaveCorrectionsResult>("/payroll/review/bulk", input),
  history: (period: string, employeeId?: string) =>
    http.get<{ history: CorrectionHistoryRow[] }>("/payroll/review/history", { period, employee_id: employeeId }),
  /** One employee's pay worked out step by step with the unsaved changes applied; nothing is written. */
  calculation: (period: string, employeeId: string, changes: { field: CorrectionField; value: number | null }[]) =>
    http.post<EmployeeCalculation>("/payroll/review/calculation", { period, employee_id: employeeId, changes }),
  /** Saves the month's global controls; null clears one. An employee override is never touched. */
  saveGlobals: (period: string, globals: Partial<Record<CorrectionField, number | null>>, reason?: string) =>
    http.post<{ saved: number }>("/payroll/review/globals", { period, globals, reason }),
  /** Moves the employee's basic pay on the employee record and appends it to their salary history. */
  syncBasicSalary: (input: { period: string; employee_id: string; new_basic: number; effective_from: string; reason?: string }) =>
    http.post<BasicSalarySyncResult>("/payroll/review/basic-salary", input),
};

export interface CalculationLine {
  code: string;
  name: string;
  amount: number;
  basis?: string;
}

export interface EmployeeCalculation {
  employee_id: string;
  employee_code: string;
  employee_name: string;
  period: string;
  calculated: boolean;
  /** Why the employee cannot be paid yet, when calculated is false. */
  reason?: string;
  days: {
    working_days: number;
    basis_days: number;
    present_days: number;
    paid_leave_days: number;
    unpaid_leave_days: number;
    lop_days: number;
    paid_days: number;
    overtime_hours: number;
  };
  basic_pay: number;
  salary_basis: string;
  lop_base: string;
  per_day_rate: number;
  lop_deduction: number;
  adjusted_basic: number;
  earnings: CalculationLine[];
  gross_earnings: number;
  deductions: CalculationLine[];
  total_deductions: number;
  net_pay: number;
  warnings?: string[];
  /** Leave taken in the month by type, and the remaining balance - from the leave module. */
  leave?: { casual: number; sick: number; earned: number; other: number; balance: number };
  /** The company leave policy for this employee, type by type. */
  leave_types?: LeaveTypeDetail[];
  total_paid_leave?: number;
  total_unpaid_leave?: number;
}

export interface LeaveTypeDetail {
  leave_type_id: string;
  code: string;
  name: string;
  is_paid: boolean;
  policy_entitlement: number;
  entitled: number;
  adjustments: number;
  used_this_month: number;
  used: number;
  pending: number;
  available: number;
  /** HR gave this employee a different entitlement than the company policy. */
  overridden: boolean;
}

export interface BasicSalarySyncResult {
  employee_id: string;
  previous_basic: number;
  new_basic: number;
  effective_from: string;
  history_id: string;
}

// ---- Payroll month detail: the whole month as one sheet ----

export type DetailColumnType = "text" | "number" | "money" | "date" | "datetime";
export type DetailColumnGroup = "employee" | "attendance" | "leave" | "earnings" | "adjustments" | "deductions" | "final" | "employer" | "metadata";

export interface DetailColumn {
  key: string;
  label: string;
  group: DetailColumnGroup;
  type: DetailColumnType;
  /** The correction field an editable column changes. */
  field?: CorrectionField;
  editable?: boolean;
  global?: boolean;
  hint?: string;
  total?: boolean;
  /** The payroll feature this column belongs to; hidden from the default views while the feature is off. */
  feature?: string;
}

export interface DetailInput {
  original: number;
  global?: number;
  override?: number;
  effective: number;
  source: "records" | "global" | "override";
  reason?: string;
  updated_by?: string;
  updated_at?: string;
  is_overridden: boolean;
}

export interface DetailRow {
  employee_id: string;
  payslip_id?: string;
  status: "ok" | "failed";
  fail_reason?: string;
  /** ready | needs_attention | missing_data - on the Review & Correct sheet only. */
  attention?: "ready" | "needs_attention" | "missing_data";
  issues?: ReadinessIssue[];
  /** missing_data, attendance, leave, salary. */
  categories?: string[];
  cells: Record<string, string | number>;
  inputs: Record<string, DetailInput>;
  overridden: boolean;
}

export interface DetailRun {
  id: string;
  period: string;
  period_label: string;
  financial_year: string;
  status: string;
  status_label: string;
  employee_count: number;
  processed_count: number;
  failed_count: number;
  generated_at?: string;
  generated_by?: string;
  approved_at?: string;
  approved_by?: string;
  payment_status: string;
  payment_mode?: string;
  payment_date?: string;
  calendar_days: number;
  editable: boolean;
  locked_reason?: string;
  stale: boolean;
}

export interface DetailControls {
  globals: Partial<Record<CorrectionField, number>>;
  records_available_days: number;
  calendar_days: number;
  basis: { proration_basis: string; proration_label: string; fixed_days?: number; lop_base: string; lop_label: string; round_net_pay: boolean };
  overtime: { base: string; divisor_hours: number; summary: string };
  statutory: {
    pf_enabled: boolean; pf_employee_rate: number; pf_employer_rate: number; pf_wage_ceiling: number;
    esi_enabled: boolean; esi_employee_rate: number; esi_employer_rate: number; esi_wage_ceiling: number;
    pt_enabled: boolean; pt_state?: string; tds_enabled: boolean; lwf_enabled: boolean;
  };
  /** The company's active leave types, read from the leave policy (never edited in payroll). */
  leave_policy?: LeavePolicyLine[];
}

export interface LeavePolicyLine {
  leave_type_id?: string;
  code: string;
  name: string;
  is_paid: boolean;
  annual_entitlement: number;
  monthly_limit?: number;
}

export interface OverrideRecord {
  employee_id?: string;
  employee_code?: string;
  employee_name?: string;
  field: CorrectionField;
  field_label: string;
  global?: number;
  override?: number;
  effective: number;
  reason?: string;
  updated_by?: string;
  updated_at?: string;
}

export interface PayrollDetail {
  run: DetailRun;
  controls: DetailControls;
  columns: DetailColumn[];
  rows: DetailRow[];
  totals: Record<string, number>;
  overrides: OverrideRecord[];
  summary?: SheetSummary;
  global_history?: GlobalChangeRecord[];
  /** The payroll features (ON / OFF and their configuration) and their change history. */
  features?: FeatureState[];
  feature_history?: FeatureHistoryRecord[];
}

export interface FeatureConfigValue {
  key: string;
  label: string;
  unit: "percent" | "money";
  /** The value payroll uses. */
  value: number;
  /** The company setting this overrides. */
  default: number;
  min: number;
  max: number;
  overridden: boolean;
}

export interface FeatureState {
  key: string;
  label: string;
  description: string;
  group: "general" | "statutory";
  /** What switching the feature off means, in plain words. */
  off_note: string;
  enabled: boolean;
  /** What the company's existing settings say. */
  default_enabled: boolean;
  /** An administrator decided this explicitly. */
  saved: boolean;
  config: FeatureConfigValue[];
  updated_by?: string;
  updated_at?: string;
}

export interface FeatureHistoryRecord {
  id: string;
  feature: string;
  old_enabled?: boolean;
  new_enabled: boolean;
  reason: string;
  changed_by_name?: string;
  changed_at: string;
}

export interface FeatureChange {
  enabled?: boolean;
  config?: Record<string, number>;
}

export interface SheetSummary {
  employees: number;
  ready: number;
  need_attention: number;
  missing_data: number;
  attendance_issues: number;
  leave_issues: number;
  salary_issues: number;
  estimated_net: number;
  with_overrides: number;
  /** Payroll has been generated for the month. */
  persisted: boolean;
}

export interface GlobalChangeRecord {
  id: string;
  field: CorrectionField;
  old_value?: number;
  new_value?: number;
  reason: string;
  employees_affected?: number;
  changed_by_name?: string;
  changed_at: string;
}

export interface DetailPreview {
  rows: DetailRow[];
  totals: Record<string, number>;
  problems?: string[];
}

/** globals: a number sets a global control, null clears it. */
export interface DetailChangeBody {
  globals?: Partial<Record<CorrectionField, number | null>>;
  global_reason?: string;
  changes?: CorrectionChange[];
  /** Payroll feature changes by feature key. */
  features?: Record<string, FeatureChange>;
}

/** The Review & Correct sheet: a month as one workspace, before it is generated. */
export const payrollSheetApi = {
  get: (period: string) => http.get<PayrollDetail>("/payroll/sheet", { period }),
  preview: (period: string, body: DetailChangeBody) => http.post<DetailPreview>("/payroll/sheet/preview", { period, ...body }),
  /** Saves the global controls and overrides; does not generate payroll. */
  save: (period: string, body: DetailChangeBody) => http.post<PayrollDetail>("/payroll/sheet/save", { period, ...body }),
  exportXlsx: (period: string) => http.download(`/payroll/sheet/export?period=${period}`),
};

export const payrollDetailApi = {
  get: (runId: string) => http.get<PayrollDetail>(`/payroll/runs/${runId}/detail`),
  preview: (runId: string, body: DetailChangeBody) => http.post<DetailPreview>(`/payroll/runs/${runId}/detail/preview`, body),
  recalculate: (runId: string, body: DetailChangeBody) => http.post<PayrollDetail>(`/payroll/runs/${runId}/detail/recalculate`, body),
  exportXlsx: (runId: string) => http.download(`/payroll/runs/${runId}/detail/export`),
};

/** The fields of a payroll run the months list uses. */
export type PayrollRunSummaryLike = Pick<PayrollRun, "id" | "period_year" | "period_month" | "status" | "employee_count" | "completed_at">;

// ---- Payslip templates (migration 0235): layout and visibility only ----

export type PayslipTemplateKey = "classic_associate" | "corporate_professional" | "minimal_executive" | "modern_corporate" | "detailed_payroll";

export interface PayslipComponentConfig {
  enabled: boolean;
  /** Payslip line codes never printed. */
  hidden: string[];
  /** Line codes printed first; the rest follow in the payroll's own order. */
  order: string[];
}

export interface PayslipTemplateConfig {
  header: { logo: boolean; tagline: boolean; address: boolean; phone: boolean; email: boolean; website: boolean };
  employee: {
    employee_id: boolean; department: boolean; designation: boolean; date_of_joining: boolean; branch: boolean;
    pan: boolean; uan: boolean; esic: boolean; pf_number: boolean;
  };
  attendance: {
    enabled: boolean; calendar_days: boolean; working_days: boolean; present_days: boolean; paid_leave: boolean;
    unpaid_leave: boolean; lop_days: boolean; overtime_hours: boolean;
  };
  leave: { enabled: boolean };
  earnings: PayslipComponentConfig;
  deductions: PayslipComponentConfig;
  employer: { enabled: boolean };
  amount_in_words: boolean;
  payment: { enabled: boolean; bank_name: boolean; account: boolean; ifsc: boolean; payment_date: boolean; payment_mode: boolean };
  ytd: { enabled: boolean };
  footer: { notes: boolean; note_lines: string[]; signature: boolean; company_name: boolean };
}

export interface PayslipTemplate {
  key: PayslipTemplateKey;
  name: string;
  description: string;
  status: string;
  is_default: boolean;
  version: number;
  /** The company saved its own configuration of this template. */
  customized: boolean;
  configuration: PayslipTemplateConfig;
  /** The template's built-in configuration, for "Reset to template defaults". */
  defaults: PayslipTemplateConfig;
  updated_at?: string;
}

/** A standard payslip line a template can hide or print first. */
export interface PayslipLineOption {
  code: string;
  label: string;
}

export const payslipTemplatesApi = {
  list: () => http.get<{ templates: PayslipTemplate[]; lines: { earnings: PayslipLineOption[]; deductions: PayslipLineOption[] } }>("/payroll/payslip-templates"),
  save: (key: PayslipTemplateKey, configuration: PayslipTemplateConfig) =>
    http.put<PayslipTemplate>(`/payroll/payslip-templates/${key}`, { configuration }),
  setDefault: (key: PayslipTemplateKey) => http.post<PayslipTemplate>(`/payroll/payslip-templates/${key}/default`),
  /** A PDF of the template with sample figures; an unsaved configuration may be previewed. */
  preview: (key: PayslipTemplateKey, configuration?: PayslipTemplateConfig) =>
    http.postDownload(`/payroll/payslip-templates/${key}/preview`, configuration ? { configuration } : undefined),
};
